import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Container, Typography, Box, Grid, Card, Button, Avatar } from '@mui/material';
import { Grass, Phone } from '@mui/icons-material';
import Layout from '../components/Layout';

const glassStyle = {
  background: 'rgba(255, 255, 255, 0.25)',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(255, 255, 255, 0.18)',
  borderRadius: '16px',
  boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.37)'
};

const LawnCareVancouver = () => {
  return (
    <Layout>
      <Helmet>
        <title>Lawn Care Vancouver BC | Professional Lawn Maintenance West Vancouver | Green Mountain</title>
        <meta name="description" content="Professional lawn care Vancouver BC. Weekly mowing, fertilization, weed control in West Vancouver & North Vancouver. Licensed lawn maintenance. Free quotes (604) 202-0053" />
        <meta name="keywords" content="lawn care vancouver, lawn maintenance west vancouver, lawn mowing north vancouver, fertilization vancouver bc, weed control vancouver, lawn aeration bc" />
        <link rel="canonical" href="https://greenmountainlandscaping.ca/lawn-care-vancouver" />
      </Helmet>
      
      {/* Hero Section */}
      <Box
        sx={{
          minHeight: '60vh',
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: 'url("/images/4.webp")',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            zIndex: -2
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(135deg, rgba(27, 94, 32, 0.8) 0%, rgba(46, 125, 50, 0.7) 100%)',
            zIndex: -1
          }
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ ...glassStyle, p: { xs: 3, md: 6 }, color: 'white', textAlign: 'center' }}>
            <Avatar sx={{ bgcolor: 'white', color: 'primary.main', width: 80, height: 80, mx: 'auto', mb: 3 }}>
              <Grass sx={{ fontSize: 40 }} />
            </Avatar>
            <Typography variant="h1" component="h1" sx={{ mb: 3, textShadow: '2px 2px 4px rgba(0,0,0,0.5)' }}>
              Professional Lawn Care Vancouver BC
            </Typography>
            <Typography variant="h5" sx={{ mb: 4, opacity: 0.9 }}>
              Keep your lawn healthy, green, and beautiful year-round
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button variant="contained" size="large" href="tel:+16042020053" startIcon={<Phone />} sx={{ bgcolor: 'white', color: 'primary.main' }}>
                (604) 202-0053
              </Button>
              <Button variant="outlined" size="large" href="/#contact" sx={{ borderColor: 'white', color: 'white' }}>
                Get Free Quote
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>
      
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <Typography variant="h1" component="h1" sx={{ mb: 4, color: 'primary.main' }}>
          Professional Lawn Care Vancouver BC
        </Typography>
        
        <Typography variant="body1" sx={{ mb: 4, fontSize: '1.1rem' }}>
          Green Mountain Landscaping provides comprehensive <strong>lawn care in Vancouver</strong>, West Vancouver, and North Vancouver. Our professional lawn maintenance services keep your grass healthy, green, and beautiful year-round.
        </Typography>

        <Typography variant="h2" component="h2" sx={{ mb: 3, color: 'primary.main' }}>
          Complete Lawn Maintenance Services Vancouver
        </Typography>

        <Grid container spacing={4} sx={{ mb: 6 }}>
          {[
            { title: 'Weekly Lawn Mowing Vancouver', desc: 'Professional mowing with precise cutting heights' },
            { title: 'Fertilization Programs BC', desc: 'Customized nutrition for BC climate conditions' },
            { title: 'Weed Control Vancouver', desc: 'Safe and effective weed prevention and removal' },
            { title: 'Lawn Aeration Services', desc: 'Improve soil health and grass root development' },
            { title: 'Overseeding & Repair', desc: 'Restore thin or damaged lawn areas' },
            { title: 'Seasonal Lawn Cleanup', desc: 'Spring and fall maintenance services' }
          ].map((service, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card sx={{ 
                ...glassStyle, 
                p: 3, 
                height: '100%',
                transition: 'transform 0.3s ease',
                '&:hover': { transform: 'translateY(-4px)' }
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ bgcolor: 'success.main', mr: 2, width: 48, height: 48 }}>
                    <Grass />
                  </Avatar>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>{service.title}</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">{service.desc}</Typography>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Typography variant="h3" component="h3" sx={{ mb: 3, color: 'primary.main' }}>
          Why Choose Our Lawn Care in West Vancouver?
        </Typography>
        
        <Typography variant="body1" sx={{ mb: 4 }}>
          Our <strong>lawn maintenance Vancouver</strong> team understands the unique climate challenges of British Columbia. We provide customized lawn care solutions for West Vancouver, North Vancouver, and surrounding Metro Vancouver areas.
        </Typography>

        <Box sx={{ textAlign: 'center', mt: 6, p: 4, bgcolor: 'primary.main', borderRadius: 4, color: 'white' }}>
          <Typography variant="h4" sx={{ mb: 2 }}>Ready for a Beautiful Lawn?</Typography>
          <Typography variant="body1" sx={{ mb: 3, opacity: 0.9 }}>Get your free lawn care estimate today</Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button variant="contained" size="large" href="tel:+16042020053" sx={{ bgcolor: 'white', color: 'primary.main' }}>
              Call (604) 202-0053
            </Button>
            <Button variant="outlined" size="large" href="/#contact" sx={{ borderColor: 'white', color: 'white' }}>
              Get Free Quote
            </Button>
          </Box>
        </Box>
      </Container>
    </Layout>
  );
};

export default LawnCareVancouver;